/**
 * HintWrapper용 버튼/힌트 메시지 다국어 지원
 *
 * 사용법:
 * import { getHintMessage } from '@src/shared/constants/btnMessages';
 * <HintWrapper content={getHintMessage('ADD_WORK')} content2={getHintMessage('ADD_WORK_2')} />
 */

import type { LocalizedMessages } from '../dto/EntityRepository';
import { useLanguageStore, Language } from '../store/languageStore';

export type HintMessageKey =
  // ProjectElementList
  | 'ADD_WORK'
  | 'ADD_WORK_2'
  | 'CREATE_WORK'
  | 'CREATE_WORK_2'
  | 'ADD_VISUAL_AID'
  | 'ADD_VISUAL_AID_2'
  | 'ADD_TEXT'
  | 'ADD_TEXT_2'
  | 'ADD_DESCRIPTION'
  | 'ADD_DESCRIPTION_2'
  | 'ADD_PDF'
  | 'ADD_PDF_2'
  | 'ADD_DIVIDER'
  | 'ADD_FORMATION'
  | 'ADD_CAROUSEL'
  | 'REORDER_ELEMENTS'
  | 'UPDATE_ELEMENTS'
  | 'NAVIGATE_TO_PROJECTS'
  | 'SCROLL_PROJECT_DETAIL_TOP'
  // About
  | 'DOWNLOAD_CV_PDF'
  | 'REORDER_CAREER_SECTIONS'
  // WorkList
  | 'DOWNLOAD_WORK_LIST_PDF'
  | 'REORDER_WORK_LIST'
  | 'CHANGE_WORK_VIEW'
  | 'IMPORT_LINKED_WORKS'
  | 'REORDER_TEXT_LIST'
  | 'CREATE_GROUP_MEMBER'
  | 'UPDATE_GROUP_MEMBER'
  | 'REORDER_GROUP_MEMBERS'
  | 'MEMBER_STATUS_ACTIVE'
  | 'MEMBER_STATUS_INACTIVE'
  | 'INVITE_ACCOUNT'
  | 'CANCEL_INVITATION'
  | 'CONNECT_ACCOUNT'
  | 'DISCONNECT_ACCOUNT'
  | 'CONNECT_VENUE'
  | 'DISCONNECT_VENUE'
  | 'CONNECT_URL'
  | 'EDIT_URL'
  | 'ABOUT_ENG_VISIBILITY_EXPAND'
  | 'ABOUT_ENG_VISIBILITY_HIDE'
  | 'WORK_ENG_VISIBILITY_EXPAND'
  | 'WORK_ENG_VISIBILITY_HIDE'
  // ProjectList
  | 'CREATE_PROJECT'
  | 'CHANGE_PROJECT_VIEW'
  | 'REORDER_PROJECTS'
  // WorkList
  | 'CHANGE_TAGS'
  | 'ADD_TAG'
  | 'TAG_MATCH_ALL_HINT'
  | 'TAG_MATCH_ANY_HINT'
  | 'ADD_DETAIL_CUT_WORK'
  | 'UPDATE_DETAIL_CUTS'
  | 'DELETE_SELECTED_WORKS'
  | 'UPDATE_SELECTED_WORKS'
  | 'REORDER_DETAIL_CUTS'
  // WorkDetail (Image/Video toggle)
  | 'CHANGE_DETAIL_TYPE'
  // TextList
  | 'CREATE_TEXT'
  | 'IMPORT_LINKED_TEXTS'
  | 'UPDATE_SELECTED_TEXTS'
  | 'DELETE_SELECTED_TEXTS'
  // FullImageViewer
  | 'ZOOM_IN'
  | 'ZOOM_IN_2'
  | 'ZOOM_OUT'
  | 'ZOOM_OUT_2'
  | 'DOWNLOAD_IMAGE'
  // Settings
  | 'GENERATE_QR_CODE'
  // TextViewer
  | 'ADD_TEXT_BLOCK'
  | 'REORDER_TEXT_BLOCKS'
  | 'UPDATE_TEXT'
  | 'DELETE_TEXT'
  // ProjectInfoList
  | 'ADD_INFO_VALUE'
  | 'REMOVE_INFO_VALUE'
  | 'DELETE_INFO'
  | 'EDIT_INFO'
  | 'UPDATE_INFO'
  | 'CREATE_NEW_INFO'
  | 'REORDER_INFO'
  // Edit mode (RightBottomIsland, OnBoardingRightBottomIsland)
  | 'VIEW_MODE_CMD_E'
  | 'EDIT_MODE_CMD_E'
  // HintSmallWrapper (visibility toggle)
  | 'SET_TO_PRIVATE'
  | 'SET_TO_PUBLIC'
  // WorkViewer (dimensions type toggle)
  | 'SET_DIMENSIONS_VARIABLE'
  | 'UNSET_DIMENSIONS_VARIABLE'
  // URL handle validation (useValidation validateHandle)
  | 'VALIDATION_HANDLE_SPACES'
  | 'VALIDATION_HANDLE_FORMAT'
  | 'VALIDATION_HANDLE_LENGTH'
  | 'VALIDATION_HANDLE_RESERVED'
  // Tag name (useValidation: validateTagName / validateTagNameInput)
  | 'VALIDATION_TAG_NAME_TRIM'
  | 'VALIDATION_TAG_NAME_LENGTH'
  | 'VALIDATION_TAG_NAME_INVALID_CHARS'
  | 'VALIDATION_TAG_NAME_EDGE_SEPARATOR'
  | 'VALIDATION_TAG_NAME_DOUBLE_SPACE'
  | 'VALIDATION_TAG_NAME_DOUBLE_HYPHEN'
  | 'VALIDATION_TAG_NAME_BETWEEN_WORDS'
  // Navigation / FloatingIsland
  | 'BACK_TO_ARCHITRAVE'; 

const hintMessages: LocalizedMessages<HintMessageKey> = {
  ADD_WORK: {
    [Language.EN]: "Import Work.",
    [Language.KR]: "Work 가져오기.",
  },
  ADD_WORK_2: {
    [Language.EN]: "Synced across pages. Managed in Works.",
    [Language.KR]: "Works에서 관리되며 모든 페이지에 반영됩니다.",
  },
  CREATE_WORK: {
    [Language.EN]: "Add Work.",
    [Language.KR]: "Work 추가하기.",
  },
  CREATE_WORK_2: {
    [Language.EN]: "Predefined filename formats help with auto-fill.",
    [Language.KR]: "정해진 형식의 파일명은 자동 입력에 도움이 됩니다.",
  },
  CREATE_GROUP_MEMBER: {
    [Language.EN]: "Add group member.",
    [Language.KR]: "그룹 멤버 추가.",
  },
  UPDATE_GROUP_MEMBER: {
    [Language.EN]: "Save group members",
    [Language.KR]: "그룹 멤버 저장",
  },
  REORDER_GROUP_MEMBERS: {
    [Language.EN]: "Reorder group members.",
    [Language.KR]: "그룹 멤버 순서 변경.",
  },
  MEMBER_STATUS_ACTIVE: {
    [Language.EN]: "Set to Active",
    [Language.KR]: "활성화하기",
  },
  MEMBER_STATUS_INACTIVE: {
    [Language.EN]: "Set to Inactive",
    [Language.KR]: "비활성화하기",
  },
  INVITE_ACCOUNT: {
    [Language.EN]: "Invite account",
    [Language.KR]: "계정 초대하기",
  },
  CANCEL_INVITATION: {
    [Language.EN]: "Cancel invitation",
    [Language.KR]: "초대 취소하기",
  },
  CONNECT_ACCOUNT: {
    [Language.EN]: "Connect account",
    [Language.KR]: "계정 연결하기",
  },
  DISCONNECT_ACCOUNT: {
    [Language.EN]: "Disconnect account",
    [Language.KR]: "계정 연결 해제",
  },
  CONNECT_VENUE: {
    [Language.EN]: "Connect venue",
    [Language.KR]: "장소 연결하기",
  },
  DISCONNECT_VENUE: {
    [Language.EN]: "Disconnect venue",
    [Language.KR]: "장소 연결 해제",
  },
  CONNECT_URL: {
    [Language.EN]: "Connect URL",
    [Language.KR]: "URL 연결하기",
  },
  EDIT_URL: {
    [Language.EN]: "Edit URL",
    [Language.KR]: "URL 수정하기",
  },
  CHANGE_TAGS: {
    [Language.EN]: "Change labels",
    [Language.KR]: "라벨 수정",
  },
  ADD_TAG: {
    [Language.EN]: "Add label",
    [Language.KR]: "라벨 추가",
  },
  TAG_MATCH_ALL_HINT: {
    [Language.EN]: "Works that include all selected labels.",
    [Language.KR]: "선택한 라벨을 모두 포함한 Work.",
  },
  TAG_MATCH_ANY_HINT: {
    [Language.EN]: "Works that include at least one selected label.",
    [Language.KR]: "선택한 라벨 중 하나 이상 포함한 Work.",
  },
  ADD_VISUAL_AID: {
    [Language.EN]: "Add a Visual Aid to support this project.",
    [Language.KR]: "프로젝트와 관련된 시각 자료.",
  },
  ADD_VISUAL_AID_2: {
    [Language.EN]: "Only in this project. e.g. references, posters or exhibition views, etc. ",
    [Language.KR]: "예) 레퍼런스 이미지, 포스터, 전시 전경 등.",
  },
  ADD_TEXT: {
    [Language.EN]: "Import Text.",
    [Language.KR]: "Text 가져오기.",
  },
  ADD_TEXT_2: {
    [Language.EN]: "Synced across pages. Managed in Texts.",
    [Language.KR]: "Texts에서 관리되며 모든 페이지에 반영됩니다.",
  },
  ADD_DESCRIPTION: {
    [Language.EN]: "Add a Description to this project.",
    [Language.KR]: "간단한 글 및 설명.",
  },
  ADD_DESCRIPTION_2: {
    [Language.EN]: "Only in this project. e.g. project note, brief description, context, etc.",
    [Language.KR]: "예) 프로젝트와 관련된 노트, 간단한 설명, 배경 등.",
  },
  ADD_PDF: {
    [Language.EN]: "Add a PDF to this project.",
    [Language.KR]: "프로젝트와 관련된 PDF 파일.",
  },
  ADD_PDF_2: {
    [Language.EN]: "Only in this project. Visitors can download the uploaded PDF.",
    [Language.KR]: "방문자가 업로드된 PDF를 다운로드할 수 있습니다.",
  },
  ADD_DIVIDER: {
    [Language.EN]: "Add a Divider to separate sections",
    [Language.KR]: "구분선 추가",
  },
  ADD_FORMATION: {
    [Language.EN]: "Arrange works in various formats.",
    [Language.KR]: "다양한 포멧으로 작품을 배치하기.",
  },
  ADD_CAROUSEL: {
    [Language.EN]: "Show images one at a time.",
    [Language.KR]: "이미지를 한 장씩 넘겨 보여 줍니다.",
  },
  REORDER_ELEMENTS: {
    [Language.EN]: "Reorder elements",
    [Language.KR]: "Element 순서 변경",
  },
  UPDATE_ELEMENTS: {
    [Language.EN]: "Save elements",
    [Language.KR]: "Element 저장",
  },
  NAVIGATE_TO_PROJECTS: {
    [Language.EN]: "Back to Projects",
    [Language.KR]: "Projects로 이동",
  },
  SCROLL_PROJECT_DETAIL_TOP: {
    [Language.EN]: "Back to top",
    [Language.KR]: "맨 위로",
  },
  DOWNLOAD_CV_PDF: {
    [Language.EN]: "Download CV",
    [Language.KR]: "CV 다운로드",
  },
  REORDER_CAREER_SECTIONS: {
    [Language.EN]: "Reorder career sections",
    [Language.KR]: "Career 섹션 순서 변경",
  },
  DOWNLOAD_WORK_LIST_PDF: {
    [Language.EN]: "Download Work List",
    [Language.KR]: "작품 리스트 다운로드",
  },
  REORDER_WORK_LIST: {
    [Language.EN]: "Reorder works",
    [Language.KR]: "Work 순서 변경",
  },
  CHANGE_WORK_VIEW: {
    [Language.EN]: "Change work view",
    [Language.KR]: "Work 목록 형식 변경",
  },
  REORDER_TEXT_LIST: {
    [Language.EN]: "Reorder texts",
    [Language.KR]: "Text 순서 변경",
  },
  ABOUT_ENG_VISIBILITY_EXPAND: {
    [Language.EN]: "Set ENG to public",
    [Language.KR]: "영문 버전 공개",
  },
  ABOUT_ENG_VISIBILITY_HIDE: {
    [Language.EN]: "Set ENG to private",
    [Language.KR]: "영문 버전 비공개",
  },
  WORK_ENG_VISIBILITY_EXPAND: {
    [Language.EN]: "Set Work ENG to public",
    [Language.KR]: "영문 Work 정보 공개",
  },
  WORK_ENG_VISIBILITY_HIDE: {
    [Language.EN]: "Set Work ENG to private",
    [Language.KR]: "영문 Work 정보 비공개",
  },
  CREATE_PROJECT: {
    [Language.EN]: "Create a new project",
    [Language.KR]: "Project 만들기",
  },
  CHANGE_PROJECT_VIEW: {
    [Language.EN]: "Change project View",
    [Language.KR]: "Project 목록 형식 변경",
  },
  REORDER_PROJECTS: {
    [Language.EN]: "Reorder projects",
    [Language.KR]: "Project 순서 변경",
  },
  ADD_DETAIL_CUT_WORK: {
    [Language.EN]: "Add Detail Cut",
    [Language.KR]: "Detail Cut 추가",
  },
  UPDATE_DETAIL_CUTS: {
    [Language.EN]: "Save Detail Cuts",
    [Language.KR]: "Detail Cut 저장",
  },
  DELETE_SELECTED_WORKS: {
    [Language.EN]: "Delete selected works",
    [Language.KR]: "선택한 Work 전체 삭제",
  },
  UPDATE_SELECTED_WORKS: {
    [Language.EN]: "Apply to selected works",
    [Language.KR]: "선택한 Work에 변경사항 적용",
  },
  REORDER_DETAIL_CUTS: {
    [Language.EN]: "Reorder Detail Cuts",
    [Language.KR]: "Detail Cut 순서 변경",
  },
  CHANGE_DETAIL_TYPE: {
    [Language.EN]: "Switch to {detailType}",
    [Language.KR]: "{detailType}로 전환",
  },
  CREATE_TEXT: {
    [Language.EN]: "Create Text",
    [Language.KR]: "Text 추가",
  },
  IMPORT_LINKED_TEXTS: {
    [Language.EN]: "Request Texts from a connected account",
    [Language.KR]: "연결된 계정의 Text 가져오기 요청",
  },
  IMPORT_LINKED_WORKS: {
    [Language.EN]: "Request Works from a connected account",
    [Language.KR]: "연결된 계정의 Work 가져오기 요청",
  },
  UPDATE_SELECTED_TEXTS: {
    [Language.EN]: "Apply to selected texts",
    [Language.KR]: "선택한 Text에 변경사항 적용",
  },
  DELETE_SELECTED_TEXTS: {
    [Language.EN]: "Delete selected texts",
    [Language.KR]: "선택한 Text 전체 삭제",
  },
  ZOOM_IN: {
    [Language.EN]: "Zoom In",
    [Language.KR]: "확대",
  },
  ZOOM_IN_2: {
    [Language.EN]: "Click the image to zoom in",
    [Language.KR]: "이미지를 클릭하여 확대",
  },
  ZOOM_OUT: {
    [Language.EN]: "Zoom Out",
    [Language.KR]: "축소",
  },
  ZOOM_OUT_2: {
    [Language.EN]: "Shift + Click image to zoom out",
    [Language.KR]: "Shift + 이미지 클릭하여 축소",
  },
  DOWNLOAD_IMAGE: {
    [Language.EN]: "Download Image",
    [Language.KR]: "다운로드",
  },
  GENERATE_QR_CODE: {
    [Language.EN]: "Generate QR Code",
    [Language.KR]: "QR Code 생성",
  },
  BACK_TO_ARCHITRAVE: {
    [Language.EN]: "Back to Architrave",
    [Language.KR]: "Architrave로 돌아가기",
  },
  ADD_TEXT_BLOCK: {
    [Language.EN]: "Add a new text block",
    [Language.KR]: "Text Block 추가",
  },
  REORDER_TEXT_BLOCKS: {
    [Language.EN]: "Reorder text blocks",
    [Language.KR]: "Text Block 순서 변경",
  },
  UPDATE_TEXT: {
    [Language.EN]: "Update Text",
    [Language.KR]: "Text 저장",
  },
  DELETE_TEXT: {
    [Language.EN]: "Delete Text",
    [Language.KR]: "Text 삭제",
  },
  ADD_INFO_VALUE: {
    [Language.EN]: "Add row",
    [Language.KR]: "행 추가",
  },
  REMOVE_INFO_VALUE: {
    [Language.EN]: "Remove row",
    [Language.KR]: "행 삭제",
  },
  DELETE_INFO: {
    [Language.EN]: "Delete info",
    [Language.KR]: "Info 삭제",
  },
  EDIT_INFO: {
    [Language.EN]: "Edit info",
    [Language.KR]: "Info 수정",
  },
  UPDATE_INFO: {
    [Language.EN]: "Save info",
    [Language.KR]: "Info 저장",
  },
  CREATE_NEW_INFO: {
    [Language.EN]: "Create a new info",
    [Language.KR]: "Project 관련 간단한 Info 추가",
  },
  REORDER_INFO: {
    [Language.EN]: "Reorder info",
    [Language.KR]: "Info 순서 변경",
  },
  VIEW_MODE_CMD_E: {
    [Language.EN]: "View Mode (Cmd + E)",
    [Language.KR]: "보기 모드 (Cmd + E)",
  },
  EDIT_MODE_CMD_E: {
    [Language.EN]: "Edit Mode (Cmd + E)",
    [Language.KR]: "편집 모드 (Cmd + E)",
  },
  SET_TO_PRIVATE: {
    [Language.EN]: "Set to Private",
    [Language.KR]: "비공개로 설정",
  },
  SET_TO_PUBLIC: {
    [Language.EN]: "Set to Public",
    [Language.KR]: "공개로 설정",
  },
  SET_DIMENSIONS_VARIABLE: {
    [Language.EN]: "Set variable",
    [Language.KR]: "가변크기 설정",
  },
  UNSET_DIMENSIONS_VARIABLE: {
    [Language.EN]: "Unset variable",
    [Language.KR]: "가변 설정 해제",
  },
  VALIDATION_HANDLE_SPACES: {
    [Language.EN]: "Spaces aren't allowed. Use underscores (_) instead.",
    [Language.KR]: "공백은 사용할 수 없습니다. 밑줄(_)을 사용해주세요.",
  },
  VALIDATION_HANDLE_FORMAT: {
    [Language.EN]: "Use lowercase letters, numbers, and underscores (_).",
    [Language.KR]: "영문 소문자(a-z), 숫자(0-9), 밑줄(_)만 입력할 수 있습니다.",
  },
  VALIDATION_HANDLE_LENGTH: {
    [Language.EN]: "URL name must be 3-30 characters long.",
    [Language.KR]: "3~30자로 입력해주세요.",
  },
  VALIDATION_HANDLE_RESERVED: {
    [Language.EN]: "This URL name is reserved. Please choose another.",
    [Language.KR]: "이미 사용 중인 값입니다.",
  },
  VALIDATION_TAG_NAME_TRIM: {
    [Language.EN]: "No leading or trailing spaces.",
    [Language.KR]: "앞뒤 공백은 사용할 수 없습니다.",
  },
  VALIDATION_TAG_NAME_LENGTH: {
    [Language.EN]: "Up to {maxLength} characters.",
    [Language.KR]: "최대 {maxLength}자까지 입력할 수 있습니다.",
  },
  VALIDATION_TAG_NAME_INVALID_CHARS: {
    [Language.EN]: "Only letters, numbers, spaces, and hyphens are allowed.",
    [Language.KR]: "문자, 숫자, 공백, 하이픈(-)만 사용할 수 있습니다.",
  },
  VALIDATION_TAG_NAME_EDGE_SEPARATOR: {
    [Language.EN]: "Can't start or end with a space or hyphen.",
    [Language.KR]: "맨 앞이나 맨 뒤에는 공백이나 하이픈(-)을 둘 수 없습니다.",
  },
  VALIDATION_TAG_NAME_DOUBLE_SPACE: {
    [Language.EN]: "Use only one space between words.",
    [Language.KR]: "단어 사이에는 공백을 하나만 넣어주세요.",
  },
  VALIDATION_TAG_NAME_DOUBLE_HYPHEN: {
    [Language.EN]: "Use only one hyphen between words.",
    [Language.KR]: "단어 사이에는 하이픈(-)을 하나만 넣어주세요.",
  },
  VALIDATION_TAG_NAME_BETWEEN_WORDS: {
    [Language.EN]: "Separate words with one space or one hyphen—not both together.",
    [Language.KR]: "단어 사이는 공백 하나 또는 하이픈(-) 하나로만 구분해주세요.",
  },
};

/**
 * HintWrapper용 메시지를 가져오는 함수
 * 언어는 languageStore에서 자동으로 가져옵니다.
 */
export function getHintMessage(
  key: HintMessageKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = hintMessages[key];

  if (!message) {
    console.warn(`Btn message key "${key}" not found.`);
    return key;
  }

  let translatedMessage = message[language] || message[Language.EN];

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translatedMessage = translatedMessage.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        String(paramValue)
      );
    });
  }

  return translatedMessage;
}
